import React, { useState } from 'react';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Utensils, MapPin, Clock, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { getInitials, getNotificationIcon } from './NotificationUtils';
import { ReviewProposalDialog } from './ReviewProposalDialog';
import type { NotificationItem } from '@/hooks/useNotifications';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

interface ProposalNotificationItemProps {
  notification: NotificationItem;
  isUnread: boolean;
  onClose: () => void;
}

export function ProposalNotificationItem({ notification, isUnread, onClose }: ProposalNotificationItemProps) {
  const navigate = useNavigate();
  const { data, type } = notification;
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);

  const handleViewEntity = () => {
    if (data.entity_type === 'burger' && data.entity_id) {
      navigate(`/burgers/${data.entity_id}`);
    } else if (data.entity_type === 'producer' && data.entity_id) {
      navigate(`/producers/${data.entity_id}`);
    }
    onClose();
  };

  const handleReviewClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setReviewDialogOpen(true);
  };

  const isBurger = type === 'burger_suggestion';
  const IconComponent = isBurger ? Utensils : MapPin;
  const iconBgClass = isBurger 
    ? "bg-primary/10" 
    : "bg-green-100 dark:bg-green-900/20";
  const iconTextClass = isBurger 
    ? "text-primary" 
    : "text-green-600 dark:text-green-400";

  return (
    <>
      <div
        className={cn(
          "flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-colors cursor-pointer",
          isUnread && "bg-primary/5 border-l-2 border-primary"
        )}
        onClick={handleViewEntity}
      >
        {data.proposer_avatar_url ? (
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage src={data.proposer_avatar_url} />
            <AvatarFallback className="bg-primary/10 text-primary">
              {getInitials(data.proposer_name)}
            </AvatarFallback>
          </Avatar>
        ) : (
          <div className={cn("h-12 w-12 rounded-full flex items-center justify-center", iconBgClass)}>
            <IconComponent className={cn("h-6 w-6", iconTextClass)} />
          </div>
        )}
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <div className="flex items-center gap-1">
              {getNotificationIcon(type)}
              <p className={cn(
                "text-foreground text-sm",
                isUnread ? "font-semibold" : "font-medium"
              )}>
                {data.proposer_name || 'Someone'} suggested {data.field_type === 'image' ? 'an image' : `a ${data.field_type === 'website_url' ? 'website' : data.field_type === 'instagram_url' ? 'Instagram' : 'description'}`} for "{data.entity_name}"
              </p>
            </div>
          </div>
          {data.field_type === 'image' && data.proposed_description ? (
            <img src={data.proposed_description} alt="Proposed image" className="h-16 w-16 rounded object-cover mt-1" />
          ) : data.proposed_description ? (
            <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
              "{data.proposed_description}"
            </p>
          ) : null}
          <div className="flex items-center justify-between mt-2">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>
                {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
              </span>
            </div>
            {data.proposal_id && (
              <Button
                variant="secondary"
                size="sm"
                className="h-7 text-xs"
                onClick={handleReviewClick}
              >
                <Eye className="h-3 w-3 mr-1" />
                Review
              </Button>
            )}
          </div>
        </div>
      </div>

      {data.proposal_id && (
        <ReviewProposalDialog
          open={reviewDialogOpen}
          onOpenChange={setReviewDialogOpen}
          proposalId={data.proposal_id}
          entityType={data.entity_type as 'burger' | 'producer'}
          entityId={data.entity_id || ''}
          entitySlug={data.entity_slug || ''}
          entityName={data.entity_name || ''}
          proposedValue={data.proposed_description || ''}
          fieldType={(data.field_type as 'description' | 'website_url' | 'instagram_url' | 'image') || 'description'}
          proposerId={data.proposer_id || ''}
          proposerName={data.proposer_name || null}
          proposerAvatarUrl={data.proposer_avatar_url || null}
          onActionComplete={onClose}
        />
      )}
    </>
  );
}
